// src/LoginPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAuth,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
} from "firebase/auth";
import { app } from "./firebase/firebaseConfig";
import { useAuth } from "./firebase/AuthContext";

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    background: "linear-gradient(to bottom, #001f3f, #0074D9)",
  },
  card: {
    backgroundColor: "#fff",
    padding: "2rem",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
    width: "100%",
    maxWidth: "420px",
    textAlign: "center",
  },
  logoWrapper: {
    marginBottom: "1rem",
    width: "200px",
    height: "200px",
    marginLeft: "auto",
    marginRight: "auto",
    borderRadius: "50%",
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 0 30px rgba(0, 0, 0, 0.3)",
  },
  logo: { width: "150px" },
  title: {
    marginBottom: "1rem",
    fontSize: "1.6rem",
    fontWeight: "bold",
    color: "#003366",
  },
  form: { display: "flex", flexDirection: "column", gap: "0.8rem" },
  input: {
    padding: "0.8rem",
    fontSize: "1rem",
    borderRadius: "6px",
    border: "1px solid #ccc",
  },
  button: {
    padding: "0.8rem",
    fontSize: "1rem",
    backgroundColor: "#003366",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  googleButton: {
    width: "100%",
    padding: "0.8rem",
    fontSize: "1rem",
    backgroundColor: "#4285F4",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: 600,
  },
  toggleText: { marginTop: "1rem", fontSize: "0.9rem" },
  dividerRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    margin: "1rem 0",
    color: "#777",
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    backgroundColor: "#ccc",
  },
  error: { color: "red", fontSize: "0.85rem", marginBottom: "1rem" },
};

const auth = getAuth(app);

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const {
    user: authUser,
    loading: authLoading,
    authInitialized,
    userDocLoading,
    firestoreBlocked,
  } = useAuth();
  const navigate = useNavigate();

  // AuthContext is the single owner of user-document initialization. Once it
  // has finished, leave /login and let ProtectedRoute handle onboarding/access.
  useEffect(() => {
    if (!authInitialized || authLoading || userDocLoading) return;
    if (authUser && !firestoreBlocked) {
      navigate({ pathname: "/", search: "" }, { replace: true });
    }
  }, [authInitialized, authLoading, authUser, userDocLoading, firestoreBlocked, navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      let msg = "Something went wrong. Please try again.";
      if (err.code === "auth/wrong-password") msg = "Incorrect password. Please try again.";
      else if (err.code === "auth/user-not-found") msg = "No account found with this email.";
      else if (err.code === "auth/invalid-email") msg = "Invalid email format.";
      else if (err.code === "auth/invalid-credential") msg = "Invalid email or password. Please try again.";
      else if (err.code === "auth/too-many-requests") msg = "Too many attempts. Please wait and try again later.";
      setError(msg);
      console.error("LoginPage: Email/Password error:", err);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    try {
      // Desktop popup
      await signInWithPopup(auth, provider);
    } catch (err) {
      // popup blocked/closed -> redirect
      if (err?.code === "auth/popup-blocked" || err?.code === "auth/popup-closed-by-user") {
        try {
          await signInWithRedirect(auth, provider);
          // Redirect flow will be handled by the useEffect when authUser appears
        } catch (e) {
          console.error("Google redirect initiation error:", e);
          setError("Google login failed. Please try again.");
        }
      } else {
        console.error("Google popup error:", err);
        setError("Google login failed. Please try again.");
      }
    }
  };

  if (authLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={styles.title}>Loading...</p>
          <p>Checking authentication status. Please wait.</p>
        </div>
      </div>
    );
  }

  if (authUser) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={styles.title}>
            {firestoreBlocked ? "Account setup needs attention" : "Finishing sign in..."}
          </p>
          <p>
            {firestoreBlocked
              ? "Google sign-in succeeded, but your account profile could not be loaded. Please contact support."
              : "Preparing your account. Please wait."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logoWrapper}>
          <img src="/playswithguru-logo.png" alt="PlaysWithGuru" style={styles.logo} />
        </div>

        <h2 style={styles.title}>Welcome to PlaysWithGuru</h2>
        {error && <p style={styles.error}>{error}</p>}

        <button onClick={handleGoogleLogin} style={styles.googleButton}>
          Continue with Google
        </button>

        <div style={styles.dividerRow}>
          <div style={styles.dividerLine} />
          <span>OR</span>
          <div style={styles.dividerLine} />
        </div>

        <form onSubmit={handleEmailLogin} style={styles.form}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            required
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            required
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
          />
          <button type="submit" style={styles.button}>
            Log In
          </button>
        </form>


        <div style={styles.toggleText}>
          New to PlaysWithGuru? Use <strong>Continue with Google</strong> above to create your account.
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
